import { Token } from './types'
import { SchoolBookingType } from './types-bookings'

export type SchoolInformation = {
    id?: string
    account?: {
        createdBy?: string
        dateRegistered?: string
        registeredBy?: string
        state?: SchoolAccountState
    }
    businessManager?: {
        contact?: string
        email?: string
        invoiceEmail?: string
        name?: string
        state?: string
        updatedBy?: string
    }
    details?: SchoolDetails
    location?: {
        address?: string
        country?: string
        lat?: number
        locationState?: string
        long?: number
        postCode?: string
        suburb?: string
    }
    logo?: {
        state?: string
        value?: string
    }
    parking?: {
        address?: string
        information?: string
        lat?: number
        long?: number
        type?: string
    }
    times?: {
        [start: string]: string
    } | null
    primaryTimes?: {
        [start: string]: string
    } & {
        same?: boolean
    }
    secondaryTimes?: {
        [start: string]: string
    } & {
        same?: boolean
        primarySame?: boolean
    }

    policies?: {
        documents?: {
            [policyId: string]: SchoolPolicy
        }
        publicUrl?: string
        policyUrl?: string
        timeUpdated?: number
        updatedBy?: string
    }

    multipleLocations?: Record<string, SchoolMultipleLocation>

    profile?: Partial<SchoolProfile>
    favourites?: Record<string, boolean>
}

export type SchoolAutoReplaceSetting = {
    primarySupportStaff: boolean
    primaryTeachers: boolean
    secondarySupportStaff: boolean
    secondaryTeachers: boolean
}

export const convertSchoolAutoReplaceSettingKey = (
    name: keyof SchoolAutoReplaceSetting
): { type: SchoolBookingType; setting: 'school' | 'supportStaff' } => {
    switch (name) {
        case 'primarySupportStaff': {
            return { type: 'Primary', setting: 'supportStaff' }
        }
        case 'primaryTeachers': {
            return { type: 'Primary', setting: 'school' }
        }
        case 'secondarySupportStaff': {
            return { type: 'Secondary', setting: 'supportStaff' }
        }
        case 'secondaryTeachers': {
            return { type: 'Secondary', setting: 'school' }
        }
    }
}

export const autoReplaceSettingMap: Record<
    keyof SchoolAutoReplaceSetting,
    string
> = {
    primarySupportStaff: 'primary support staff',
    primaryTeachers: 'primary teachers',
    secondarySupportStaff: 'secondary support staff',
    secondaryTeachers: 'secondary teachers',
} as const

export type SchoolDetails = {
    activeAutoReplacements?: Partial<SchoolAutoReplaceSetting>
    contactNumber?: string
    educationType?: string
    privateOrPublic?: string
    religion?: string
    schoolName?: string
    schoolType?: SchoolType_v2
    updatedBy?: string
    contactType?: SchoolContactType | null
}
export type SchoolProfile = {
    aboutSchool: string
    teachingEnvSupport: TeachingEnvSupport
}

export type ChipMap<T extends string> = Partial<Record<T, boolean>>

export type TeachingEnvSupport = {
    setup: ChipMap<TeachingEnvSupport_setup_keys>
    context: ChipMap<TeachingEnvSupport_context_keys>
    support: ChipMap<TeachingEnvSupport_support_keys>
    expectations: ChipMap<TeachingEnvSupport_expectations_keys>
}

export type ChipLabelMap = Record<string, string>

//#region Teaching Environment Support
export const teachingEnvSupport_setup: ChipLabelMap = {
    lessonPlans: 'Lesson plans',
    seatingPlans: 'Seating plans',
    compositeClasses: 'Composite classes',
    educationSupportStaff: 'Education Support Staff (ES/LSOs)',
} as const
type TeachingEnvSupport_setup_keys = keyof typeof teachingEnvSupport_setup

export const teachingEnvSupport_context: ChipLabelMap = {
    highEALD: 'High EAL/D population',
    individualPlans: 'Individual learning plans common',
    dedicatedUnit: 'Dedicated inclusion/support unit onsite',
    traumaInformedPractices: 'Trauma-informed practices in place',
    swpbsSchool: 'SWPBS school',
} as const
type TeachingEnvSupport_context_keys = keyof typeof teachingEnvSupport_context

export const teachingEnvSupport_support: ChipLabelMap = {
    onsiteContact: 'On-site contact person',
    leadershipAccess: 'Leadership accessible',
    techAccess: 'Technology access provided',
} as const
type TeachingEnvSupport_support_keys = keyof typeof teachingEnvSupport_support

export const teachingEnvSupport_expectations: ChipLabelMap = {
    proAttire: 'Business/professional attire',
    smartCasual: 'Smart Casual',
    modestDress: 'Modest dress expectations',
    closedShoes: 'Closed-toe shoes required',
    tattoosCovered: 'Visible tattoos should be covered',
    noFacialPiercings: 'Facial piercings not permitted',
    noDressRequirement: 'No specific dress requirements',
} as const
type TeachingEnvSupport_expectations_keys =
    keyof typeof teachingEnvSupport_expectations

export const teachingEnvSupportLabelMap: Record<
    keyof TeachingEnvSupport,
    ChipLabelMap
> = {
    setup: teachingEnvSupport_setup,
    context: teachingEnvSupport_context,
    support: teachingEnvSupport_support,
    expectations: teachingEnvSupport_expectations,
} as const
//#endregion

type SchoolMultipleLocation = {
    isDefaultLocation: boolean
    location: {
        address: string
        country: string
        lat: number
        locationState: string
        long: number
        postCode: string
        suburb: string
    }
    name: string
}

export type SchoolPolicy = {
    id?: string
    createdByUserUid?: string
    timeCreated?: number
    timeUpdated?: number
    title?: string
    updatedBy?: string
    url?: string

    publicUrl?: string
    policyUrl?: string
}

export type SchoolUser = {
    id?: string
    deviceSettings?: {
        deviceType?: 'iOS' | 'android' | 'web'
        loggedIn?: boolean
        version?: string
    }
    notificationSettings?: SchoolUserNotifications
    school?: {
        accountState?: SchoolUserAccountState
        authority?: SchoolUserAuthority
        dateRegistered?: string
        positionTitle?: string
        schoolCountry?: string
        schoolState?: string
        schoolUid?: string
    }
    termsAndConditions?: {
        dateAccepted?: string
        documentID?: string
        timeAccepted?: number
    }
    userDetails?: {
        contact?: string
        email?: string
        firstName?: string
        lastName?: string
        profilePictureURL?: string | null
        type?: string | null
    }
    tokens?: Token
}

export type SchoolLocations = {
    [id: string]: {
        g?: string
        l?: any
    }
}

export const SchoolAccountStates = {
    pendingApproval: 'pendingApproval',
    approved: 'approved',
    removed: 'removed',
    onHold: 'onHold',
} as const
export type SchoolAccountState =
    (typeof SchoolAccountStates)[keyof typeof SchoolAccountStates]

export const SchoolUserAccountStates = {
    pendingSparkApproval: 'pendingSparkApproval',
    pendingSchoolAdminApproval: 'pendingSchoolAdminApproval',
    approved: 'approved',
    removed: 'removed',
    deleted: 'deleted',
} as const
export type SchoolUserAccountState =
    (typeof SchoolUserAccountStates)[keyof typeof SchoolUserAccountStates]

export const SchoolUserAuthorities = {
    admin: 'admin',
    bookingAssistant: 'bookingAssistant',
} as const
export type SchoolUserAuthority =
    (typeof SchoolUserAuthorities)[keyof typeof SchoolUserAuthorities]

export enum UserDataState {
    INCOMPLETE = 'incomplete',
    SUBMITTED = 'submitted',
    APPROVED = 'approved',
    EXPIRED = 'expired',
    REJECTED = 'rejected',
}

export const SchoolTypes = {
    Primary: 'Primary',
    Secondary: 'Secondary',
    'Primary & Secondary': 'Primary & Secondary',
} as const
export type SchoolType_v2 = (typeof SchoolTypes)[keyof typeof SchoolTypes]

export enum SchoolType {
    PRIMARY = 'Primary',
    SECONDARY = 'Secondary',
    PRIMARY_SECONDARY = 'Primary & Secondary',
}

export enum SchoolSector {
    PRIVATE = 'Private',
    PUBLIC = 'Public',
}

export enum SchoolReligion {
    NONE = 'None',
    ANGLICAN = 'Anglican',
    BRETHREN = 'Brethren',
    CATHOLIC = 'Catholic',
    SECULAR = 'Secular',
    OTHER = 'Other',
}

export enum SchoolEducationType {
    COED = 'Co-Education',
    GIRLS = 'Girls Only',
    BOYS = 'Boys Only',
}

export enum SchoolContact {
    USER = 'Use my number',
    LANDLINE = 'Add Landline Number',
    MOBILE = 'Add Mobile Number',
}
export const SchoolContactTypes = {
    'Use my number': 'Use my number',
    'Add Landline Number': 'Add Landline Number',
    'Add Mobile Number': 'Add Mobile Number',
} as const
export type SchoolContactType =
    (typeof SchoolContactTypes)[keyof typeof SchoolContactTypes]

export type SchoolUserNotifications = {
    bookingConfirmations?: boolean
    cancellationNotifications?: boolean
    completedBookings?: boolean
    isEnabled?: boolean
    schoolUpdates?: boolean
    sparkNews?: boolean
}

export const ParkingTypes = {
    'Staff Car Park': 'Staff Car Park',
    'On Street Parking': 'On Street Parking',
    'Public Car Park': 'Public Car Park',
} as const
export type ParkingType = (typeof ParkingTypes)[keyof typeof ParkingTypes]

export type SchoolInvoice = {
    invoiceId?: string
    amount?: number
    dueDate?: string
    invoiceDate?: string
    invoiceNumber?: string
    state?: SchoolInvoiceState
}
export enum SchoolInvoiceState {
    ISSUED = 'issued',
    PAID = 'paid',
    OVERDUE = 'overdue',
}

export type SchoolInvoiceRequest = {
    email?: string
    invoiceDueDate?: string
    invoiceIssueDate?: string
    invoiceNumber?: string
    requestedBy?: string
    schoolName?: string
    timeStamp?: string
    userUid?: string
}

export type BookableTeachersForDate = {
    [teacherUid: string]: {
        Primary?: 'available'
        Secondary?: 'available'
    }
}
export type BookableTeachers = {
    [date: string]: BookableTeachersForDate
}

export type BookableTeacherAvailability = {
    Primary?: {
        [date: string]: true
    }
    Secondary?: {
        [date: string]: true
    }
}
